/**
 * La mascota del Resumen, que además elige el color del panel.
 *
 * Pulsarla abre los colores a su derecha; al elegir uno, el panel entero cambia,
 * los colores se cierran y ella se pone contenta.
 *
 * ── Por qué aquí ────────────────────────────────────────────────────────────
 * El color se elige también desde el menú de usuario, que es donde va a buscarlo
 * quien sabe que existe. Esto es lo otro: la mascota es lo primero que se ve al
 * entrar y ya reacciona a todo —sigue el puntero, salta al pasar por encima,
 * se infla al pulsarla—, así que es donde alguien prueba a pulsar sin buscar
 * nada. Que el gesto tenga premio es lo que hace que se descubra la función.
 *
 * Los dos sitios comparten los mismos puntos de color y la misma escritura de la
 * preferencia; ver `accent.ts`.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { ACCENTS, ACCENT_SWATCHES, accentLabelKey, persistAccent, useAccent, type VelticaAccent } from '@webbuilder/ui'
import { AiMascot } from '../CreateWithAiModal/AiMascot'
import { useI18n } from '../../lib/i18n'

export function MascotColorPicker({ gaze }: {
  /**
   * `up` mientras haya un aviso esperando respuesta justo encima.
   *
   * La mascota deja de seguir al puntero y levanta la vista hacia él. Es lo que
   * convierte un recuadro más del panel en algo que alguien está señalando, sin
   * una palabra de más — y con la frase del globo diciendo qué es, las dos cosas
   * cuentan lo mismo por caminos distintos.
   */
  gaze?: 'up' | undefined
} = {}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  /* Leído de la preferencia, no copiado al montar: el mismo color se elige desde
     el menú de usuario, y con una copia propia esto seguía marcando el anterior. */
  const accent = useAccent()
  /* Un contador y no un booleano: pedir el gesto dos veces seguidas —dos colores
     seguidos— tiene que hacerlo dos veces. Ver `AiMascot`. */
  const [cheer, setCheer] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      /* De vuelta a la mascota: quien cerró con el teclado se quedaría sin foco
         en mitad de la página. */
      triggerRef.current?.focus()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const choose = (option: VelticaAccent) => {
    persistAccent(option)
    setOpen(false)
    /* La alegría después de cerrar, no antes: con los colores todavía abiertos,
       el gesto queda medio tapado por el propio panel que lo provocó. */
    setCheer((count) => count + 1)
  }

  return (
    <div className="dash-mascot-picker" ref={rootRef}>
      <button
        ref={triggerRef}
        className="dash-mascot-picker-trigger"
        type="button"
        aria-label={t('mascotColor.change')}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {/* Al abrir los colores vuelve a mirar al frente: lo que hay que ver
            entonces es la fila de puntos que acaba de salir a su derecha, no un
            aviso de arriba. */}
        <AiMascot cheer={cheer} gaze={open ? undefined : gaze} />
      </button>

      {open ? (
        <div className="dash-mascot-picker-pop" role="dialog" aria-label={t('mascotColor.title')}>
          <span className="dash-mascot-picker-title">{t('mascotColor.title')}</span>
          {/* Los mismos puntos que el menú de usuario, con sus clases: son la
              misma decisión en dos sitios y tienen que verse iguales. */}
          <div className="dash-accent-swatches">
            {ACCENTS.map((option) => (
              <button
                key={option}
                type="button"
                className={`dash-accent-swatch${option === accent ? ' is-active' : ''}`}
                style={{ '--dash-accent-swatch': ACCENT_SWATCHES[option] } as CSSProperties}
                aria-label={t(accentLabelKey(option))}
                aria-pressed={option === accent}
                title={t(accentLabelKey(option))}
                onClick={() => choose(option)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
